// src/pages/Themes.js
import React, { useState, useEffect } from 'react';
import { Check, Palette } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

export default function Themes() {
  const [themes, setThemes] = useState([]);
  const [activeTheme, setActiveTheme] = useState('');
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const { applyTheme } = useAuth();

  useEffect(() => {
    Promise.all([
      api.get('/themes'),
      api.get('/settings'),
    ]).then(([t, s]) => {
      setThemes(t.data);
      setActiveTheme(s.data?.general?.default_theme?.value || '');
      setLoading(false);
    });
  }, []);

  const activate = async (theme) => {
    try {
      await api.put(`/themes/${theme.id}/activate`);
      setActiveTheme(theme.name);
      applyTheme(theme);
      toast.success(`Temă "${theme.display_name}" activată`);
    } catch (e) { toast.error('Eroare la activare'); }
  };

  if (loading) return <div className="loading-center"><span className="spinner" /></div>;

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">Teme</div><div className="page-subtitle">Personalizează aspectul aplicației</div></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
        {themes.map(t => {
          const isActive = t.name === activeTheme;
          let vars = {};
          try { vars = typeof t.css_variables === 'string' ? JSON.parse(t.css_variables) : t.css_variables; } catch {}
          return (
            <div key={t.id} className="card card-hover" style={{ border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`, position: 'relative', overflow: 'hidden' }}>
              {isActive && (
                <div style={{ position: 'absolute', top: 12, right: 12, width: 24, height: 24, background: 'var(--accent)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Check size={14} color="white" />
                </div>
              )}
              {/* Preview */}
              <div style={{ height: 100, borderRadius: 8, overflow: 'hidden', marginBottom: 16, background: vars['--bg-primary'] || '#111', border: '1px solid #ffffff10', display: 'flex', gap: 0 }}>
                <div style={{ width: 70, background: vars['--bg-secondary'] || '#1a1a2e', padding: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {[vars['--accent'], vars['--text-secondary'], vars['--text-secondary'], vars['--text-secondary']].map((c, i) => (
                    <div key={i} style={{ height: 6, borderRadius: 3, background: c || '#555', opacity: i === 0 ? 1 : 0.4 }} />
                  ))}
                </div>
                <div style={{ flex: 1, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {['--accent', '--success', '--warning', '--danger'].map(k => (
                      <div key={k} style={{ width: 16, height: 16, borderRadius: 4, background: vars[k] || '#333' }} />
                    ))}
                  </div>
                  <div style={{ height: 8, background: vars['--bg-card'] || '#222', borderRadius: 4, width: '80%' }} />
                  <div style={{ height: 8, background: vars['--bg-hover'] || '#333', borderRadius: 4, width: '60%' }} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 14, height: 14, borderRadius: '50%', background: t.preview_color || vars['--accent'] || '#4f8ef7' }} />
                <span style={{ fontWeight: 700, fontSize: 15 }}>{t.display_name}</span>
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>{t.description}</p>
              <button className={`btn ${isActive ? 'btn-success' : 'btn-secondary'}`} style={{ width: '100%', justifyContent: 'center' }} onClick={() => !isActive && activate(t)}>
                {isActive ? <><Check size={14} /> Activ</> : <><Palette size={14} /> Activează</>}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
